import React from 'react'
import { DateTime } from 'luxon'
import { ZodSafeParseResult, z } from 'zod'
import {
  MembershipInvitationError,
  MembershipSeat,
  MembershipSeatError,
  MutatedMembershipInvitation,
  AppendableSubscription,
} from '@/services/MembershipService/MembershipService'
import { Offer } from '@/services/PifService'
import { GuildSale } from '@/services/SaleService'
import { logger } from '@/utils/logging'
import { TranslateFunction } from '@/utils/translate/translate-client'
import { Result } from '@/utils/types'
import { GuildStatus, getEmptyGuildStatus } from '@/utils/users/usersServer'
import { BASIC_TIER, GuildTier } from './tiers'

export interface State {
  locale: string
  t: TranslateFunction
  // NOTE - for guild checkout context
  shouldShowAvailabilityWarning: boolean
  offer: Offer
  sale: GuildSale | null
  guildStatus: GuildStatus
  appendableSubscription: AppendableSubscription | null
  screen: GuildGiftScreen
  seats: Record<
    string,
    {
      /**
       * ID of either the seat (for add seat flow) or gift intent (for stripe checkout flow)
       */
      id: string
      state: string
      name: string
      email: string
      message: string
      scheduleAt: string
      /**
       * Supports the display of validation errors only after submit. Thereafter,
       * validations will display updates inline as typing.
       */
      isSubmitted: boolean
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      validation?: ZodSafeParseResult<any>
      /**
       * Seat polled from seats ready page
       */
      seatResult?: Result<MembershipSeat | undefined, MembershipSeatError>
      /**
       * Invitation created when saving on seats ready page
       */
      invitationResult?: Result<MutatedMembershipInvitation, MembershipInvitationError>
      /**
       * Seats Ready UI accordian drawer open flag
       */
      isOpen: boolean
    }
  >
  tier: GuildTier
}

export type Seat = State['seats'][string]

export type Action =
  | TransitionScreenAction
  | AssocSeatAction
  | InvitationSubmitAction
  | InvitationCreatedAction
  | SeatQueriedAction
  | SeatToggleAction
  | SeatsAddedAction

interface TransitionScreenAction {
  type: 'transition'
  screen: GuildGiftScreen
}

interface AssocSeatAction {
  type: 'assoc-seat'
  id: string
  name: string
  value: string
}

interface InvitationSubmitAction {
  type: 'invitation-submit'
  id: string
}

interface InvitationCreatedAction {
  type: 'invitation-created'
  id: string
  result: Result<MutatedMembershipInvitation, MembershipInvitationError>
}

interface SeatQueriedAction {
  type: 'seat-queried'
  id: string
  result: Result<MembershipSeat | undefined, MembershipSeatError>
}

interface SeatsAddedAction {
  type: 'seats-added'
  result: Result<MembershipSeat[], MembershipSeatError>
}

interface SeatToggleAction {
  type: 'seat-toggle'
  id: string
}

export const SCREENS = ['payment', 'seats'] as const
export type GuildGiftScreen = (typeof SCREENS)[number]
export const MAX_MESSAGE_CHARS_LENGTH = 200

export function ensureScreen(maybeScreen: unknown) {
  return isScreen(maybeScreen) ? maybeScreen : DEFAULT_STATE.screen

  function isScreen(str: unknown): str is GuildGiftScreen {
    return typeof str === 'string' && (SCREENS as readonly string[]).includes(str)
  }
}

export const DEFAULT_STATE = {
  locale: 'en',
  t: (() => 'defaultTranslation') as TranslateFunction,
  shouldShowAvailabilityWarning: false,
  offer: {
    id: 'defaultOfferId',
    enabled: false,
    currency: 'USD',
    region: 'US',
    name: 'Default Offer',
    price: 0,
    slug: 'default-offer-slug',
    offeredproductSet: [],
  },
  sale: null,
  appendableSubscription: null,
  guildStatus: getEmptyGuildStatus(),
  screen: 'payment' as const,
  seats: {},
  tier: BASIC_TIER,
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'transition':
      return reduceTransition(state, action)
    case 'assoc-seat':
      return reduceAssocGiftIntent(state, action)
    case 'invitation-submit':
      return reduceInvitationSubmit(state, action)
    case 'invitation-created':
      return reduceInvitationCreated(state, action)
    case 'seat-queried':
      return reduceSeatQueried(state, action)
    case 'seat-toggle':
      return reduceSeatToggle(state, action)
    case 'seats-added':
      return reduceSeatsAdded(state, action)
  }

  function reduceInvitationSubmit(state: State, action: InvitationSubmitAction): State {
    const seat = state.seats[action.id]
    if (!seat) throw new Error('No matching seat. Cannot reduce invitation submit.')

    return {
      ...state,
      seats: {
        ...state.seats,
        [action.id]: {
          ...seat,
          isSubmitted: true,
        },
      },
    }
  }

  function reduceInvitationCreated(state: State, action: InvitationCreatedAction): State {
    const seat = state.seats[action.id]
    if (!seat) throw new Error('No matching seat. Cannot reduce invitation created.')

    return {
      ...state,
      seats: {
        ...state.seats,
        [action.id]: {
          ...seat,
          // DESIGN NOTE - if there's an invitationResult, which starts as nil, it means that
          // a save has worked previously on the form. If fails, continue to show what existed
          // before. Event handler will relay via toast that there was an error
          invitationResult:
            !seat?.invitationResult || (!!seat.invitationResult && action.result.ok)
              ? action.result
              : seat.invitationResult,
        },
      },
    }
  }

  function reduceTransition(state: State, action: TransitionScreenAction): State {
    return {
      ...state,
      screen: action.screen,
    }
  }

  function reduceAssocGiftIntent(state: State, action: AssocSeatAction): State {
    const seat = state.seats[action.id]
    if (!seat) throw new Error('No matching seat. Cannot reduce assoc seat.')

    const newSeat = {
      ...seat,
      [action.name]: action.value,
    }
    return {
      ...state,
      seats: {
        ...state.seats,
        [action.id]: {
          ...newSeat,
          validation: NewSeatSchema.safeParse(newSeat),
        },
      },
    }
  }

  function reduceSeatQueried(state: State, action: SeatQueriedAction): State {
    const seat = state.seats[action.id]
    if (!seat) throw new Error('No matching seat. Cannot reduce seat queried.')

    return {
      ...state,
      seats: {
        ...state.seats,
        [action.id]: {
          ...seat,
          seatResult: action.result,
        },
      },
    }
  }

  function reduceSeatToggle(state: State, action: SeatToggleAction): State {
    const seat = state.seats[action.id]
    if (!seat) throw new Error('No matching seat. Cannot reduce seat toggle.')

    return {
      ...state,
      seats: {
        ...state.seats,
        [action.id]: {
          ...seat,
          isOpen: !state.seats[action.id]?.isOpen,
        },
      },
    }
  }

  function reduceSeatsAdded(state: State, action: SeatsAddedAction): State {
    // DESIGN NOTE - there is no current good reason to hang onto the error in state.
    // The error is shown in a toast UI, and the user remains on the PaymentScreen.
    if (!action.result.ok) return state

    return {
      ...state,
      seats: {
        ...state.seats,
        ...Object.fromEntries(action.result.value.map((seat) => [seat.id, mapSeatState(seat)])),
      },
    }

    function mapSeatState(seat: MembershipSeat) {
      const seatState = {
        id: seat.id,
        state: seat.id,
        name: seat.membershipInvitation?.recipientName ?? '',
        email: seat.membershipInvitation?.recipientEmail ?? '',
        message:
          seat.membershipInvitation?.recipientMessage ??
          state.t(
            'iHopeYouEnjoyThisMembership',
            'I hope you enjoy this membership to Angel. Our family loves watching Angel movies together, and we thought you might as well.',
          ),
        scheduleAt: seat.membershipInvitation?.sendAt.split('T')[0] ?? today(),
        isSubmitted: false,
        isOpen: false,
        invitationResult: seat.membershipInvitation
          ? { ok: true as const, value: seat.membershipInvitation }
          : undefined,
      }
      return {
        ...seatState,
        validation: NewSeatSchema.safeParse(seatState),
      }
    }
  }
}

export const GuildGiftContext = React.createContext<{
  state: State
  dispatch: React.Dispatch<Action>
}>({
  state: DEFAULT_STATE,
  dispatch: () => {},
})

const SeatFields = z.object({
  name: z.string().nonempty({ error: 'Recipient name required' }),
  email: z.string().nonempty({ error: 'Recipient email required' }).email({ error: 'Invalid recipient email' }),
  message: z.string().max(MAX_MESSAGE_CHARS_LENGTH),
  scheduleAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    error: 'Valid date required',
  }),
})
export const NewSeatSchema = SeatFields.superRefine(validateScheduleAtDate)

function validateScheduleAtDate(val: z.infer<typeof SeatFields>, ctx: z.RefinementCtx) {
  if (val.scheduleAt && val.scheduleAt.trim() !== '') {
    const d = DateTime.fromISO(val.scheduleAt).startOf('day').toJSDate()
    if (isNaN(d.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Invalid date',
      })
      return
    }
    const min = DateTime.now().startOf('day').toJSDate()
    const max = DateTime.fromJSDate(min).plus({ month: 1 }).toJSDate()

    if (d < min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Date must not be in the past',
      })
    }

    if (d > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Date must be no later than 1 month from today',
      })
    }
  }
}

export function today(): string {
  return DateTime.now().toFormat('yyyy-MM-dd')
}

/**
 * We send at a standard time of 13:00 UTC, which is either 6am MST or 7am MDT.
 * If early morning, we wait until 7am. If it's late in the afternoon, it'll send
 * immediately on the very next cron. If scheduled out for tomorrow or beyond,
 * it'll send at 7am on that date.
 */
export function formatSendAt(seat: Seat) {
  return seat.scheduleAt + 'T13:00:00.000Z'
}

export function isMembershipInvitationExist(seat: Seat) {
  return seat.invitationResult?.ok && !!seat.invitationResult.value.id
}

export function mapCreateMembershipInvitationInput(seat: Seat) {
  return {
    recipientEmail: seat.email,
    recipientMessage: seat.message,
    recipientName: seat.name,
    sendAt: formatSendAt(seat),
  }
}

export function mapSeatId(state: State, seat: Seat) {
  if (isAddSeatFlow(state)) return seat.id

  if (!(seat.seatResult?.ok && seat.seatResult.value?.id)) {
    const msg = 'Invalid seat membership seat state'
    logger().error(msg, { giftIntent: seat })
    throw new Error(msg)
  }

  return seat.seatResult.value.id
}

export function mapInvitationId(seat: Seat) {
  if (!(seat.invitationResult?.ok && seat.invitationResult.value.id)) {
    const msg = 'Invalid seat invitation state'
    logger().error(msg, { giftIntent: seat })
    throw new Error(msg)
  }

  return seat.invitationResult.value.id
}

/**
 * The same State shape is used for both the Stripe flow and the Add Seat flow.
 * But some things look the same but represent different data between the flows.
 * A bit of a hack. Well, this is the easiest/clearest way to
 * distinguish between the flows.
 */
export function isAddSeatFlow(state: State) {
  return !!state.appendableSubscription
}
